import type { NearbyUser } from "@/types";

/**
 * The decision that turns a page of nearby people into the list on screen.
 *
 * Extracted from `session-store` for the same reason `chat-session-merge` was:
 * a small rule with a sharp failure mode — a duplicated row is a FlatList key
 * collision — that cannot be exercised through the store without a React
 * Native runtime. (`nearbyTotalAfterPage` lived here too until the step-10
 * count removal: the headcount it protected no longer exists, the chip
 * derives "N+" from the rendered list and the cursor.)
 */

/**
 * Fold the next page into the list already rendered.
 *
 * Merged by id rather than concatenated. The server pages on `user_id`, a key
 * that is unique and cannot change, so the pages are disjoint whenever the
 * server is right. This is for when it is not: a duplicate id reaches the
 * FlatList as a key collision, and React renders the same person twice with no
 * error anywhere to say why.
 *
 * Order is the order rows arrived, and a repeated row keeps its original
 * position while taking the newer page's data. Moving it to the end would make
 * a person jump down the list mid-scroll, which is the exact thing paging on an
 * immutable key exists to prevent.
 */
export function mergeNearbyPage(
  current: readonly NearbyUser[],
  page: readonly NearbyUser[],
): NearbyUser[] {
  const byId = new Map(current.map((person) => [person.id, person]));
  for (const person of page) byId.set(person.id, person);
  return [...byId.values()];
}
